<template>
  <v-dialog
    width="100%"
    max-width="1400px"
    v-model="dialog"
    scrollable
    @keydown.esc="dialog = false"
  >
    <v-card :loading="loading" v-if="item">
      <v-system-bar window dark
        ><span>Sammelrechnung {{ item.number }} </span><v-spacer />
        <v-btn icon @click="dialog = false"
          ><v-icon>mdi-close</v-icon></v-btn
        ></v-system-bar
      >

      <TableComponent
        :items="item.invoices"
        :loading="loading"
        @showDetails="$emit('showDetails', $event)"
        @showEdit="$emit('showEdit', $event)"
        showEditBtn
        :group="false"
      >
      </TableComponent>
      <v-divider></v-divider>

      <v-list>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle
              >Sammelrechnung ({{ statusDescription }})</v-list-item-subtitle
            >
            <v-list-item-title>
              {{ item.description }}
              <v-btn icon small @click="editDescription"
                ><v-icon>mdi-pen</v-icon></v-btn
              ></v-list-item-title
            >
          </v-list-item-content>
          <CurrencyValue :value="total" />
        </v-list-item>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle> iban</v-list-item-subtitle>
            <v-list-item-title> {{ iban }} </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle>Status</v-list-item-subtitle>
            <v-list-item-title> {{ statusDescription }}</v-list-item-title>
          </v-list-item-content>

          <ReportButton
            v-if="statusCode != 'created'"
            resource="report/invoicegroup"
            :parameters="{ id: item.id }"
            text
            outlined
            dense
            color="red"
            >Rechnung</ReportButton
          >
        </v-list-item>
      </v-list>

      <v-divider />
      <v-card-actions>
        <template v-if="statusCode == 'submitted'">
          <v-btn text color="danger" @click="ungroup" :loading="working"
            >ungruppieren</v-btn
          >
          <v-btn
            text
            color="error"
            :loading="working"
            @click="
              doAction({
                action: 'reject',
                message:
                  'Sollen die Rechnungen an den/die Mitarbeiter/in zurückgewiesen werden?',
                color: 'error',
              })
            "
            >Zurückweisen</v-btn
          >
          <v-btn
            text
            color="success"
            :loading="working"
            @click="
              doAction({
                action: 'submitSap',
                message: 'Sollen die Rechnungen an SAP übermittelt werden?',
                color: 'success',
              })
            "
          >
            Übermitteln
          </v-btn>
        </template>

        <template v-if="statusCode == 'submittedSap'">
          <v-btn
            text
            color="error"
            @click="
              doAction({
                action: 'rejectSap',
                message: 'Sollen alle Rechnungen in SAP zurückgewiesen werden?',
                color: 'error',
              })
            "
            >Zurückweisen</v-btn
          >
          <v-btn
            text
            color="success"
            @click="
              doAction({
                action: 'enter',
                message: 'Sollen alle Rechnungen verbucht werden?',
                color: 'success',
              })
            "
            >verbuchen</v-btn
          >
        </template>

        <v-spacer />
        <v-btn text @click="dialog = false">schliessen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from "vue";
import CurrencyValue from "common/components/CurrencyValue.vue";
import ReportButton from "@/components/ReportButton.vue";
import TableComponent from "./TableComponent.vue";

import { formatDatespan } from "common/utils/date.js";

export default defineComponent({
  components: {
    CurrencyValue,
    ReportButton,
    TableComponent,
  },
  props: ["id"],
  data: () => ({
    dialog: true,
    item: null,
    loading: false,
    working: false,
  }),
  computed: {
    iban() {
      if (!this.item) return "";
      return [...new Set(this.item.invoices.map((el) => el.iban))].join(", ");
    },
    statusCode() {
      if (!this.item || !this.item.invoices) return null;
      const statuses = new Set(this.item.invoices.map((el) => el.status.code));
      if (statuses.size === 1) {
        return statuses.values().next().value;
      }
      return "mixed";
    },
    statusDescription() {
      if (!this.item || !this.item.invoices) return null;
      const statuses = new Set(
        this.item.invoices.map((el) => el.status.description)
      );
      if (statuses.size === 1) {
        return statuses.values().next().value;
      }
      return "mixed";
    },
    total() {
      if (!this.item || !this.item.invoices) return null;
      const total = this.item.invoices.reduce(
        (sum, invoice) => sum + invoice.amount,
        0
      );
      return total;
    },
  },

  watch: {
    dialog() {
      if (!this.dialog) {
        const parentRoute = this.$route.matched.slice(-2).shift();
        this.$router.push({
          name: parentRoute.name,
        });
      }
    },
    id() {
      this.fetchData();
    },
  },
  methods: {
    async editDescription() {
      const description = await this.$root.prompt({
        message: "Beschreibung der Sammelrechnung bearbeiten",
        color: "info",
        icon: "mdi-pen",
        label: "Beschreibung",
        value: this.item.description,
      });
      if (description) {
        this.working = true;
        await this.apiPatch({
          resource: "finance/invoicegroup",
          id: this.id,
          key: "description",
          value: description,
        });
        await this.fetchData();
        this.$emit("update");
        this.working = false;
      }
    },
    async ungroup() {
      if (
        await this.$root.confirm({
          message:
            "Sollen die Rechnungen aus der Gruppe gelöst und einzeln weiterverarbeitet werden?",
          color: "danger",
        })
      ) {
        this.working = true;

        await this.apiDelete({
          resource: "finance/invoicegroup",
          id: this.id,
        });

        this.$emit("update");
        this.working = false;
        this.dialog = false;
      }
    },
    async doAction({ action, ...args }) {
      if (await this.$root.confirm(args)) {
        this.working = true;
        for await (const invoice of this.item.invoices) {
          await this.apiPatch({
            resource: "finance/invoice",
            id: invoice.id,
            key: "action",
            value: action,
          });
        }
        this.$emit("update");
        this.working = false;
        this.dialog = false;
      }
    },

    formatDatespan,
    async fetchData() {
      this.loading = true;
      this.item = await this.apiGet({
        resource: "finance/invoicegroup",
        id: this.id,
      });
      this.loading = false;
    },
  },

  async created() {
    this.fetchData();
  },
});
</script>
